/**
 * Client-side alert filter for the Feed view's browser notifications.
 *
 * Mirrors the two-stage filter applied by sendTelegramAlert() on the server
 * (server/edge_manager/functions/src/functions/faceRecognition.ts):
 *
 *   Stage 1 — access scope: admins see all faces; non-admins are restricted
 *             to faces whose classificationId is in their classificationIds.
 *   Stage 2 — per-classification alert preference: if alertClassificationIds
 *             is undefined, accept any face from stage 1 ("all accessible");
 *             if [], opt-out (no alerts); otherwise only faces with a
 *             classificationId in the set.
 *
 * Keep this in lockstep with the server filter — if the server logic changes,
 * update here too.
 */

export interface AlertableFace {
  classificationId: string
  isUnmatched?: boolean
  personId?: string | null
}

export interface MemberAlertContext {
  isAdmin: boolean
  alertsEnabled: boolean
  classificationIds: string[]
  // unset = "all accessible". Arrives as `null` (not `undefined`) when read via
  // a Firebase callable, whose response serializes `undefined` fields to `null`.
  alertClassificationIds?: string[] | null
}

/**
 * Returns the subset of faces a member should be notified about for a given
 * sighting. Empty array means "do not notify this user for this sighting".
 */
export function filterAlertableFaces(
  faces: AlertableFace[],
  member: MemberAlertContext
): AlertableFace[] {
  if (!member.alertsEnabled) return []

  // Stage 1: access scope
  let relevant = member.isAdmin
    ? faces
    : faces.filter((f) => member.classificationIds.includes(f.classificationId))

  // Stage 2: per-classification alert preference.
  // `!= null` (not `!== undefined`) on purpose: getGroupDetails returns this
  // field unset for the default "alert on all" preference, and the Firebase
  // callable boundary turns that `undefined` into `null` before it reaches the
  // client. Treating only `undefined` as "all" let a `null` slip through to
  // `.length` below — crashing the onSnapshot handler, which silently killed
  // both the chime/notification and the feed's live updates.
  if (member.alertClassificationIds != null) {
    if (member.alertClassificationIds.length === 0) return []
    const preferred = new Set(member.alertClassificationIds)
    relevant = relevant.filter((f) => preferred.has(f.classificationId))
  }

  return relevant
}

export function shouldNotify(
  faces: AlertableFace[],
  member: MemberAlertContext
): boolean {
  return filterAlertableFaces(faces, member).length > 0
}
